import { TimeWindow } from '../types/time-window.type';

// Rolling window used when a caller names none: the Train A.I. bell count of topics with
// pending reviews, and Core's hot-topic aggregate and conversation reads when no
// from/to is passed. The Train A.I. page itself follows its time-window chips.
export const HOT_TOPIC_OVERVIEW_WINDOW_DAYS = 7;

// The time-window chips every Train A.I. studio shows, and the one it opens with. One
// list for Chat, Ticket and Voice so the page behaves the same in every studio. There
// is no All time: Review loads a topic's questions unpaginated, so 90 days is the
// largest window it may load at once.
export const TRAIN_AI_TIME_WINDOWS = [
  TimeWindow.Last24Hours,
  TimeWindow.Last7Days,
  TimeWindow.Last30Days,
  TimeWindow.Last90Days,
] as const;

export type TTrainAITimeWindow = (typeof TRAIN_AI_TIME_WINDOWS)[number];

export const isTrainAITimeWindow = (
  window: TimeWindow,
): window is TTrainAITimeWindow =>
  (TRAIN_AI_TIME_WINDOWS as readonly TimeWindow[]).includes(window);

export const DEFAULT_TRAIN_AI_TIME_WINDOW: TTrainAITimeWindow =
  TimeWindow.Last7Days;

// Fallback topic name used when a question doesn't fit any defined topic, regardless of mode.
export const HOT_TOPIC_FALLBACK_NAME = 'Undefined';
export const HOT_TOPIC_FALLBACK_NAMES = [HOT_TOPIC_FALLBACK_NAME];

// Defaults for the Review A.I. Agent nightly pre-approval, used when a company has no
// app_config.reviewAgentConfig. Both are whole percentages: a question qualifies when its
// top article scores at least similarityThresholdPercent, and the agent approves at most
// maxPreApprovalPercent of a topic's weekly volume.
export const REVIEW_AGENT_DEFAULT_CONFIG = {
  similarityThresholdPercent: 85,
  maxPreApprovalPercent: 85,
};

// Shared enum for hot topic source - whether a topic was defined by an admin
// (predefined) or discovered by the LLM at call time (ai).
// Values intentionally match HotTopicsMode so the same string is used for both
// the per-document source and the per-studio operating mode.
export enum HotTopicsMode {
  AI = 'ai',
  PREDEFINED = 'predefined',
}

// Review-state filter for the hot topic conversation endpoints.
// UNREVIEWED backs the "Review" flow (pending questions), PRE_APPROVED backs the
// pre-approved preview opened from the progress bar, ALL returns both.
export enum HotTopicConversationFilter {
  ALL = 'all',
  UNREVIEWED = 'unreviewed',
  PRE_APPROVED = 'pre-approved',
}
